"use client";
import React from "react";

import { Box, BoxDiv, BoxSx, Icon, Text } from "../../foundations";
import { useClickOutside } from "../../hooks";
import { DropdownOption } from "../TextField/DropdownMenu/types";
import { DropdownMenu } from "./DropdownMenu";

export type DropdownProps = {
  defaultOption?: DropdownOption;
  description?: string;
  disabled?: boolean;
  displayValue?: string;
  dropdownOptions: DropdownOption[];
  error?: string;
  label: string;
  noResultsMessage?: string;
  onSelectOption?: (option: DropdownOption) => void;
  readOnly?: boolean;
  required?: boolean;
  selectedOption?: DropdownOption;
  showNoResults?: boolean;
} & BoxDiv;

export const Dropdown: React.FC<DropdownProps> = ({
  defaultOption,
  description,
  disabled = false,
  displayValue,
  dropdownOptions,
  error,
  label,
  noResultsMessage = "No results found",
  onBlur,
  onSelectOption,
  readOnly = false,
  required = false,
  selectedOption: selectedOptionProp,
  showNoResults = false,
  ...rest
}) => {
  const isControlled = React.useRef(!!selectedOptionProp);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const dropdownButtonRef = React.useRef<HTMLButtonElement>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = React.useState(false);
  const [selectedOptionInternal, setSelectedOptionInternal] = React.useState<
    DropdownOption | undefined
  >(defaultOption);

  // If a selected option is ever passed, we should assume the user is only
  // using a controlled version of the Dropdown and ignore the internal state
  if (selectedOptionProp) {
    isControlled.current = true;
  }

  const selectedOption = isControlled.current
    ? selectedOptionProp
    : selectedOptionInternal;

  useClickOutside(dropdownRef, () => setIsDropdownVisible(false));

  const handleOptionSelect = (option: DropdownOption) => {
    setSelectedOptionInternal(option);
    onSelectOption?.(option);
    dropdownButtonRef.current?.focus();
    setIsDropdownVisible(false);
  };

  const handleClick = React.useCallback(() => {
    if (!disabled && !readOnly) {
      setIsDropdownVisible((prev) => !prev);
    }
  }, [disabled, readOnly]);

  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node)) {
        setIsDropdownVisible(false);
        setIsFocused(false);
        onBlur?.(event);
      }
    },
    [onBlur]
  );

  const handleContainerKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    switch (event.key) {
      case "Enter":
        event.preventDefault();
        setIsDropdownVisible((prev) => !prev);
        break;
      case "ArrowUp":
      case "ArrowDown":
        event.preventDefault();
        setIsDropdownVisible(true);
        break;
      default:
        break;
    }
  };

  const handleDropdownMenuDismiss = () => {
    dropdownButtonRef.current?.focus();
    setIsDropdownVisible(false);
  };

  const handleFocus = () => {
    if (!disabled && !readOnly) {
      setIsFocused(true);
    }
  };

  const shouldShowLabel = !!selectedOption || !!displayValue;

  const labelTextStyles = React.useMemo<BoxSx>(() => {
    const labelYPosition = shouldShowLabel ? "-24px" : "-50%";
    const labelScale = shouldShowLabel ? 0.66 : 1;
    return {
      position: "absolute",
      left: "spacing-4",
      top: "50%",
      transform: `translateY(${labelYPosition}) scale(${labelScale})`,
      transformOrigin: "left center",
      transition: "transition-ease-out-fast",
      pointerEvents: "none",
      fontSize: "font-size-1",
      lineHeight: "leading-2",
      letterSpacing: "kerning-2",
      color: disabled ? "field-text-disabled" : "field-text-label",
      whiteSpace: "nowrap",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      gap: "spacing-1"
    };
  }, [disabled, shouldShowLabel]);

  const descriptionTextStyles = React.useMemo<BoxSx>(() => {
    return {
      mt: "spacing-2",
      color: disabled ? "field-text-disabled" : "field-text-description"
    };
  }, [disabled]);

  const inputStyles = React.useMemo<BoxSx>(
    () => ({
      position: "relative",
      width: "100%",
      height: "spacing-10",
      px: "spacing-4",
      paddingTop: shouldShowLabel ? "spacing-7" : "spacing-5",
      paddingBottom: shouldShowLabel ? "spacing-3" : "spacing-5",
      borderRadius: "radius-2",
      cursor: disabled || readOnly ? "not-allowed" : "pointer",
      ...(disabled && { pointerEvents: "none" }),
      display: "flex",
      alignItems: "center",
      color: disabled
        ? "field-text-disabled"
        : readOnly
          ? "field-text-read-only"
          : "field-text-primary",
      transition: "transition-color-medium"
    }),
    [disabled, readOnly, shouldShowLabel]
  );

  const dropdownBorderStyles = React.useMemo<BoxSx>(
    () => ({
      position: "relative",
      border: "1px solid",
      borderColor: "field-stroke",
      borderRadius: "radius-2",
      cursor: disabled || readOnly ? "not-allowed" : "pointer",
      backgroundColor: error
        ? "field-surface-error"
        : disabled || readOnly
          ? "field-surface-disabled"
          : "field-surface",
      ...(readOnly && {
        borderWidth: "0 0 1px 0",
        borderRadius: "unset"
      }),
      outline: "2px solid",
      outlineColor: error ? "field-stroke-error" : "transparent",
      outlineOffset: "-2px",
      transition: "transition-color-medium",
      ...((isDropdownVisible || isFocused) && {
        outlineColor: "focus",
        borderColor: "transparent"
      }),
      ...(!(disabled || readOnly) && {
        ":focus-in": {
          outlineColor: "focus",
          borderColor: "transparent"
        }
      })
    }),
    [error, isFocused, isDropdownVisible, disabled, readOnly]
  );

  return (
    <Box
      onBlur={handleBlur}
      onFocus={handleFocus}
      sx={{ display: "flex", flexDirection: "column" }}
      {...rest}
    >
      <Box ref={dropdownRef} sx={{ position: "relative", userSelect: "none" }}>
        <Box
          role="button"
          ref={dropdownButtonRef}
          sx={dropdownBorderStyles}
          tabIndex={disabled || readOnly ? -1 : 0}
          aria-disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isDropdownVisible}
          onClick={handleClick}
          onKeyDown={handleContainerKeyDown}
        >
          <Box as="label" sx={labelTextStyles}>
            <Text variant="body-1-regular">{label}</Text>
            {required && (
              <Text
                variant="body-1-regular"
                sx={{ color: "feedback-onSurface-incidental-critical" }}
              >
                *
              </Text>
            )}
          </Box>

          <Box sx={inputStyles}>
            {displayValue ? (
              <Text variant="body-1-regular">{displayValue}</Text>
            ) : (
              selectedOption && (
                <Text variant="body-1-regular">{selectedOption?.label}</Text>
              )
            )}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                right: "spacing-4",
                transform: "translateY(-50%)",
                display: "flex"
              }}
            >
              <Icon
                icon={isDropdownVisible ? "caret-up" : "caret-down"}
                size="xs"
                color={
                  disabled || readOnly ? "control-disabled" : "field-onSurface"
                }
              />
            </Box>
          </Box>
        </Box>
        <DropdownMenu
          dropdownOptions={dropdownOptions}
          showNoResults={showNoResults}
          noResultsMessage={noResultsMessage}
          isOpen={isDropdownVisible}
          selectedOption={selectedOption}
          onOptionSelect={handleOptionSelect}
          onDismiss={handleDropdownMenuDismiss}
        />
      </Box>

      {description && (
        <Text variant="body-2-regular" sx={{ ...descriptionTextStyles }}>
          {description}
        </Text>
      )}
      {error && (
        <Text
          variant="body-2-regular"
          sx={{ mt: "spacing-2", color: "field-text-error" }}
        >
          {error}
        </Text>
      )}
    </Box>
  );
};
